import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

// CSRF double-submit: the backend also sets a (non-httpOnly) csrf_token cookie
// alongside the session cookies. A cross-site page can make the browser send our
// cookies, but can't read this one or forge a matching header itself, so echoing
// it back here is what proves the request actually came from our own frontend.
const CSRF_METHODS = new Set(["post", "put", "patch", "delete"]);
api.interceptors.request.use((config) => {
  if (CSRF_METHODS.has((config.method || "get").toLowerCase())) {
    const token = readCookie("csrf_token");
    if (token) config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

// Transparent access-token refresh: on a 401, try /auth/refresh once and
// retry the original request. Multiple concurrent 401s share one refresh
// call instead of each firing their own. If refresh also fails (refresh
// cookie expired/missing), the original 401 is passed through as-is so
// existing login-redirect handling still works.
//
// Also self-heals a stale csrf_token cookie (403 "CSRF token missing or
// invalid"): a session opened before this cookie existed, or before its
// current value was rotated, has valid access/refresh cookies but no (or a
// mismatched) csrf_token — every mutating request would 403 forever with no
// way for the user to recover short of manually logging out and back in.
// /auth/refresh always issues a fresh csrf_token on success (see
// server.py::set_auth_cookies calls), so running the same refresh-and-retry
// flow fixes this exactly like the 401 case does.
let refreshPromise = null;
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const isAuthRoute = config?.url?.includes("/auth/login") || config?.url?.includes("/auth/refresh") || config?.url?.includes("/auth/register");
    const isCsrfError = response?.status === 403 && /csrf/i.test(response?.data?.detail || "");
    if ((response?.status === 401 || isCsrfError) && config && !config._retried && !isAuthRoute) {
      config._retried = true;
      try {
        if (!refreshPromise) {
          refreshPromise = api.post("/auth/refresh").finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        return api(config);
      } catch {
        // fall through to the original error
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function inr(amount) {
  if (amount == null) return "₹0";
  return "₹" + Number(amount).toLocaleString("en-IN");
}

export const STATUS_LABELS = {
  pending_payment: "Awaiting Payment",
  paid: "Paid",
  requirements_pending: "Submit Requirements",
  requirements_submitted: "Requirements Received",
  assigned: "Designer Assigned",
  in_progress: "Design in Progress",
  delivered: "Delivered",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const STATUS_COLORS = {
  pending_payment: "bg-yellow-100 text-yellow-800",
  paid: "bg-blue-100 text-blue-800",
  requirements_pending: "bg-orange-100 text-orange-800",
  requirements_submitted: "bg-purple-100 text-purple-800",
  assigned: "bg-indigo-100 text-indigo-800",
  in_progress: "bg-blue-100 text-blue-800",
  delivered: "bg-emerald-100 text-emerald-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};
